"use client";

import { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { getCdnImagePath } from "@/utils/bus/getCdnImagePath";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";

export default function HotelSharePopup({ isOpen, onClose, hotelData }) {
  const t = useTranslationDetector("hotel");
  const [mounted, setMounted] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, copy] = useCopyToClipboard();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setShareUrl(window.location.href);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const { title, location, imageSrc } = useMemo(() => {
    const name =
      hotelData?.HotelName || hotelData?.Name || t("hotelNameFallback", { default: "Otel" });
    const city = hotelData?.City || "";
    const district = hotelData?.District || "";
    const locParts = [district, city].filter(Boolean);
    const loc = locParts.join(", ");

    const medias = hotelData?.Medias || hotelData?.HotelDetails?.Medias || [];
    let src = "";
    if (Array.isArray(medias) && medias.length > 0) {
      const first = medias[0];
      src = typeof first === "string" ? first : first?.Url || "";
    } else if (hotelData?.MainImage || hotelData?.HotelDetails?.MainImage) {
      src = hotelData?.MainImage || hotelData?.HotelDetails?.MainImage || "";
    }

    return {
      title: name,
      location: loc,
      imageSrc: src,
    };
  }, [hotelData, t]);

  const encodedUrl = encodeURIComponent(shareUrl || "");
  const encodedTitle = encodeURIComponent(title || "");
  const encodedText = encodeURIComponent(`${title}${location ? ` - ${location}` : ""}`);

  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  const xUrl = `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
  const whatsappUrl = `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`;
  const mailtoUrl = `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`;

  const facebookIcon = getCdnImagePath("/saka/common/social-Icons/facebook.svg");
  const xIcon = getCdnImagePath("/saka/common/social-Icons/x.svg");
  const whatsappIcon = getCdnImagePath("/saka/common/social-Icons/whatsapp.svg");
  const mailIcon = getCdnImagePath("/saka/common/social-Icons/mail.svg");
  const linkIcon = getCdnImagePath("/saka/common/social-Icons/link.svg");

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    await copy(shareUrl);
  }, [copy, shareUrl]);

  if (!mounted || !isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 px-4"
      role="dialog"
      aria-modal="true"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div className="relative w-full max-w-[480px] rounded-2xl bg-white shadow-xl">


        <div className="flex flex-col  gap-8 px-6 py-5">
          <div className="flex flex-col gap-6">
            <div className="flex justify-between items-center">
              <div></div>
              <h2 className="text-[20px] leading-[26px] font-semibold text-foreground text-center">
                {t("shareHotelWithLovedOnes", {
                  default: "Tesisi Sevdiklerinle Paylaş",
                })}
              </h2>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("closeSharePopup", { default: "Kapat" })}
                className="flex items-center justify-center text-foreground-muted cursor-pointer"
              >
                <FontAwesomeIcon icon={byPrefixAndName.far["xmark"]} className="h-5! w-5!" />
              </button>
            </div>

            <div className="flex w-full items-start gap-4 rounded-xl bg-white border border-border-weak p-3">
              {imageSrc ? (
                <div className="relative h-[80px] w-[120px] overflow-hidden rounded-lg bg-muted">
                  <Image
                    src={imageSrc}
                    alt={title}
                    fill
                    sizes="80px"
                    className="object-cover"
                    unoptimized
                  />
                </div>
              ) : (
                <div className="flex h-[80px] w-[120px] items-center justify-center rounded-lg bg-muted" />
              )}
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-base font-semibold leading-21 text-foreground">{title}</span>
                {location && (
                  <span className="truncate mt-[2px] text-sm leading-19 text-foreground-muted">{location}</span>
                )}
              </div>
            </div>
          </div>


          <div className="flex w-full flex-col items-center gap-4">
            <div className="grid w-full grid-cols-5 gap-3">
              <a
                href={facebookUrl}
                target="_blank"
                rel="noreferrer"
                className="flex flex-col items-center gap-2"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full text-white">
                  <Image
                    src={facebookIcon}
                    alt="Facebook"
                    width={48}
                    height={48}
                  />
                </div>
                <span className="text-base font-medium leading-21 text-foreground">Facebook</span>
              </a>

              <a
                href={xUrl}
                target="_blank"
                rel="noreferrer"
                className="flex flex-col items-center gap-2"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full text-white">
                  <Image
                    src={xIcon}
                    alt="X"
                    width={48}
                    height={48}
                  />
                </div>
                <span className="text-base font-medium leading-21 text-foreground">X</span>
              </a>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer"
                className="flex flex-col items-center gap-2"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full text-white">
                  <Image
                    src={whatsappIcon}
                    alt="Whatsapp"
                    width={48}
                    height={48}
                  />
                </div>
                <span className="text-base font-medium leading-21 text-foreground">Whatsapp</span>
              </a>

              <a
                href={mailtoUrl}
                className="flex flex-col items-center gap-2"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full  text-foreground">
                  <Image
                    src={mailIcon}
                    alt="E-Posta"
                    width={48}
                    height={48}
                  />
                </div>
                <span className="text-base font-medium leading-21 text-foreground">E-Posta</span>
              </a>

              <button
                type="button"
                onClick={handleCopy}
                className="flex flex-col items-center gap-2 cursor-pointer"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full text-foreground">
                  <Image
                    src={linkIcon}
                    alt="Bağlantıyı Kopyala"
                    width={48}
                    height={48}
                  />
                </div>
                <span className="text-base font-medium leading-21 text-foreground">
                  {copied
                    ? t("linkCopied", { default: "Kopyalandı" })
                    : t("copyLink", { default: "Bağlantıyı Kopyala" })}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

